import { useState } from "react";
import { useLicense } from "../auth/useLicense";
import { useAuth } from "../auth/useAuth";
import { reportError } from "../utils/reportError";
import { SITE_URLS, CHECKOUT_ERROR_CODES } from "../constants";
import styles from "./PaymentRequiredScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Step = "idle" | "submitting" | "error";

const ERROR_MESSAGES: Record<string, string> = {
  [CHECKOUT_ERROR_CODES.INVALID_CODE]: "That code wasn't found — check the spelling.",
  [CHECKOUT_ERROR_CODES.CODE_EXHAUSTED]: "This code has already been redeemed.",
};

function getErrorMessage(err: unknown): { message: string; code: string } {
  const code = (err as { checkoutErrorCode?: string }).checkoutErrorCode ?? "";
  const message = ERROR_MESSAGES[code] ?? "Something went wrong — please try again.";
  return { message, code };
}

type Props = {
  initialCode?: string;
  /** Called once when the user submits the form — use to clear any prefilled invite from storage. */
  onCodeSubmitted?: () => void;
};

export function PaymentRequiredScreen({ initialCode, onCodeSubmitted }: Props) {
  const { startCheckout } = useLicense();
  const { signOut } = useAuth();
  const [code, setCode] = useState(initialCode ?? "");
  const [step, setStep] = useState<Step>("idle");
  const [errorInfo, setErrorInfo] = useState<{ message: string; code: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setStep("submitting");
    setErrorInfo(null);
    try {
      onCodeSubmitted?.();
      await startCheckout(trimmed);
    } catch (err: unknown) {
      const info = getErrorMessage(err);
      if (!info.code) reportError(err, "PAYMENT_CHECKOUT_FAILED");
      setErrorInfo(info);
      setStep("error");
    }
  };

  const errorCode = errorInfo?.code ?? "";
  const showWaitlistLink = step === "error" && errorCode === CHECKOUT_ERROR_CODES.INVALID_CODE;
  const showContactLink = step === "error" && errorCode === CHECKOUT_ERROR_CODES.CODE_EXHAUSTED;

  return (
    <div className={styles.screen} data-testid={TEST_IDS.PAYMENT_REQUIRED_SCREEN}>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Beta Access</span>
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>An invite gets you in.</h1>
        <p className={styles.body}>
          Retreat Conductor is in private beta. Enter your invite code to
          continue — you're in.
        </p>

        <div className={styles.rule} />

        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            className={styles.input}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="INVITE-CODE"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            autoFocus={!initialCode}
            data-testid={TEST_IDS.PAYMENT_CODE_INPUT}
          />
          {step === "error" && errorInfo && (
            <div className={styles.error} data-testid={TEST_IDS.PAYMENT_ERROR_MSG}>
              {errorInfo.message}
              {showWaitlistLink && (
                <>
                  {" "}
                  <a
                    href={SITE_URLS.WAITLIST}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.errorLink}
                    data-testid={TEST_IDS.PAYMENT_WAITLIST_LINK}
                  >
                    Join the waitlist →
                  </a>
                </>
              )}
              {showContactLink && (
                <>
                  {" "}
                  <a
                    href={SITE_URLS.SUPPORT}
                    className={styles.errorLink}
                    data-testid={TEST_IDS.PAYMENT_CONTACT_LINK}
                  >
                    Email us →
                  </a>
                </>
              )}
            </div>
          )}
          <PrimaryAction
            type="submit"
            disabled={step === "submitting" || !code.trim()}
            testId={TEST_IDS.PAYMENT_SUBMIT_BTN}
          >
            {step === "submitting" ? "Checking…" : "Continue"}
          </PrimaryAction>
        </form>

        <p className={styles.hint}>
          No invite?{" "}
          <a href={SITE_URLS.WAITLIST} target="_blank" rel="noopener noreferrer">
            Join the waitlist.
          </a>
        </p>
        <p className={styles.legal}>
          No subscription — one payment, permanent access.{" "}
          <a href={SITE_URLS.TERMS} target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
        </p>

        <div className={styles.rule} />

        <SecondaryAction onClick={signOut}>Sign out</SecondaryAction>
      </div>
    </div>
  );
}
